import axios, { AxiosError, AxiosInstance } from "axios";
import type {
  ExpedienteAnalizado,
  HealthResponse,
  RutaLogica,
  TokenResponse,
} from "@/types";

// ─── Sesión por cookie ─────────────────────────────────────────────── //
//
// El JWT ya NO vive en localStorage (accesible a XSS) sino en una cookie
// HttpOnly que el navegador adjunta solo. Aquí sólo leemos la cookie CSRF
// (legible) para reenviarla por header en cada request mutante.

const CSRF_COOKIE = "gema_csrf";
export const EVENTO_SESION_EXPIRADA = "gema-sesion-expirada";

function leerCookie(nombre: string): string | null {
  const m = document.cookie.match(
    new RegExp("(?:^|; )" + nombre + "=([^;]*)")
  );
  return m ? decodeURIComponent(m[1]) : null;
}

// ─── Axios instance ────────────────────────────────────────────────── //

// En dev usamos `/api/...` con proxy de Vite hacia el backend (8080).
// En prod (build) leer VITE_API_BASE_URL si está configurado.
const baseURL = (import.meta.env.VITE_API_BASE_URL as string | undefined) || "/api";

const cliente: AxiosInstance = axios.create({
  baseURL,
  timeout: 60_000,
  headers: { "Content-Type": "application/json" },
  withCredentials: true, // envía y recibe la cookie de sesión
});

cliente.interceptors.request.use((config) => {
  const metodo = (config.method ?? "get").toLowerCase();
  if (["post", "put", "patch", "delete"].includes(metodo)) {
    const csrf = leerCookie(CSRF_COOKIE);
    if (csrf) config.headers["X-CSRF-Token"] = csrf;
  }
  return config;
});

cliente.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Sesión vencida o ausente — avisa al AuthProvider para redirigir.
      window.dispatchEvent(new Event(EVENTO_SESION_EXPIRADA));
    }
    return Promise.reject(error);
  }
);

/** Usuario de la sesión actual (lee la cookie). Lanza si no hay sesión. */
export async function sesionActual(): Promise<{
  sub: string;
  rol: string;
  empresa: string | null;
}> {
  const { data } = await cliente.get<{
    sub: string;
    rol: string;
    empresa: string | null;
  }>("/auth/me");
  return data;
}

/** Cierra la sesión en el servidor (borra las cookies). */
export async function cerrarSesionServidor(): Promise<void> {
  try {
    await cliente.post("/auth/logout");
  } catch {
    // Da igual si falla: el estado local se limpia de todos modos.
  }
}

// ─── Auth ──────────────────────────────────────────────────────────── //

/** Login del backoffice. El rol lo determina el servidor, no el cliente. */
export async function login(
  username: string,
  password: string
): Promise<TokenResponse> {
  const { data } = await cliente.post<TokenResponse>("/auth/login", {
    username,
    password,
  });
  return data;
}

export async function cambiarPassword(
  passwordActual: string,
  passwordNueva: string
): Promise<void> {
  await cliente.post("/auth/cambiar-password", {
    password_actual: passwordActual,
    password_nueva: passwordNueva,
  });
}

/** Solicita un enlace de restablecimiento. Responde igual exista o no la cuenta. */
export async function solicitarRecuperacion(identificador: string): Promise<void> {
  await cliente.post("/auth/recuperar", { identificador });
}

/** Fija una contraseña nueva usando el token del correo. */
export async function restablecerPassword(
  token: string,
  passwordNueva: string
): Promise<void> {
  await cliente.post("/auth/restablecer", {
    token,
    password_nueva: passwordNueva,
  });
}

/**
 * Emite un token de servicio para un conector externo.
 * Requiere rol administrador — no es la vía de acceso de las personas.
 */
export async function emitirTokenServicio(
  sub: string,
  rol: "analista" | "supervisor" | "administrador" | "sistema",
  horasVigencia = 8
): Promise<TokenResponse> {
  const { data } = await cliente.post<TokenResponse>("/auth/token", {
    sub,
    rol,
    horas_vigencia: horasVigencia,
  });
  return data;
}

// ─── Usuarios (administración) ─────────────────────────────────────── //

import type { Rol, Usuario } from "@/types";

export interface CrearUsuarioPayload {
  username: string;
  nombre: string;
  email?: string | null;
  rol: Rol;
  // AFINIA | AIR_E — obligatorio para analista/supervisor; nulo para globales.
  empresa?: string | null;
  password: string;
}

export interface ActualizarUsuarioPayload {
  nombre?: string;
  email?: string | null;
  rol?: Rol;
  empresa?: string;
  activo?: boolean;
  password?: string;
}

export async function listarUsuarios(
  incluirInactivos = false
): Promise<Usuario[]> {
  const { data } = await cliente.get<Usuario[]>("/usuarios", {
    params: { incluir_inactivos: incluirInactivos },
  });
  return data;
}

export async function crearUsuario(payload: CrearUsuarioPayload): Promise<Usuario> {
  const { data } = await cliente.post<Usuario>("/usuarios", payload);
  return data;
}

export async function actualizarUsuario(
  username: string,
  payload: ActualizarUsuarioPayload
): Promise<Usuario> {
  const { data } = await cliente.patch<Usuario>(`/usuarios/${username}`, payload);
  return data;
}

export async function desactivarUsuario(username: string): Promise<void> {
  await cliente.delete(`/usuarios/${username}`);
}

// ─── Empresas (tenants) ────────────────────────────────────────────── //

export interface PerfilEmpresa {
  empresa: string;
  nombre_legal: string;
  nit: string;
  codigo_operador: string;
  jurisdiccion: string[];
  umbral_dias_alerta_sap: number;
  umbral_dias_legal_sap: number;
  plantilla_default: string;
  plantilla_accede: string;
  plantilla_no_accede: string;
  plantilla_improcedente: string;
  nic_regex: string;
  soporte_email: string;
  web: string;
  corpus_namespace: string;
}

export interface CrearEmpresaPayload {
  codigo: string;
  nombre_legal: string;
  nit: string;
  codigo_operador: string;
  jurisdiccion: string[];
  umbral_dias_alerta_sap?: number;
  umbral_dias_legal_sap?: number;
  plantilla_default: string;
  plantilla_accede: string;
  plantilla_no_accede: string;
  plantilla_improcedente: string;
  nic_regex?: string;
  soporte_email: string;
  web: string;
  corpus_namespace: string;
}

export type ActualizarEmpresaPayload = Partial<
  Omit<CrearEmpresaPayload, "codigo">
> & { activo?: boolean };

export interface ReadinessEmpresa {
  codigo: string;
  perfil_ok: boolean;
  corpus_propio_archivos: string[];
  plantillas_branded: boolean;
  operativa_baseline: boolean;
  faltantes: string[];
}

export async function listarEmpresas(): Promise<PerfilEmpresa[]> {
  const { data } = await cliente.get<PerfilEmpresa[]>("/empresas");
  return data;
}

export async function crearEmpresa(
  payload: CrearEmpresaPayload
): Promise<PerfilEmpresa> {
  const { data } = await cliente.post<PerfilEmpresa>("/empresas", payload);
  return data;
}

export async function actualizarEmpresa(
  codigo: string,
  payload: ActualizarEmpresaPayload
): Promise<PerfilEmpresa> {
  const { data } = await cliente.patch<PerfilEmpresa>(
    `/empresas/${codigo}`,
    payload
  );
  return data;
}

export async function readinessEmpresa(codigo: string): Promise<ReadinessEmpresa> {
  const { data } = await cliente.get<ReadinessEmpresa>(
    `/empresas/${codigo}/readiness`
  );
  return data;
}

// ─── Healthz ───────────────────────────────────────────────────────── //

export async function obtenerHealth(): Promise<HealthResponse> {
  const { data } = await cliente.get<HealthResponse>("/healthz");
  return data;
}

// ─── Expedientes ───────────────────────────────────────────────────── //

export interface ListarExpedientesQuery {
  empresa?: string;
  estado?: string;
  revisor_id?: string;
  limit?: number;
}

export async function listarExpedientes(
  filtros: ListarExpedientesQuery = {}
): Promise<ExpedienteAnalizado[]> {
  const { data } = await cliente.get<ExpedienteAnalizado[]>("/expedientes", {
    params: { ...filtros, limit: filtros.limit ?? 50 },
  });
  return data;
}

export async function obtenerExpediente(
  id: string
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.get<ExpedienteAnalizado>(`/expedientes/${id}`);
  return data;
}

export interface CrearExpedienteRequest {
  empresa: string;
  numero_radicado: string;
  nic: string;
  estrato: number;
  direccion: string;
  fecha_radicado: string;
  texto_pqr: string;
  fecha_factura_reclamada?: string | null;
  fecha_respuesta_empresa?: string | null;
  lectura_factura?: number | null;
  historial?: Array<{ periodo: string; kwh_facturado: number; estimado?: boolean }>;
  texto_orden_servicio?: string | null;
  ruta_crm?: RutaLogica | null;
}

export async function crearExpediente(
  body: CrearExpedienteRequest
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    "/expedientes",
    body
  );
  return data;
}

// ─── Intake visual (subir PDFs al chat) ──────────────────────────── //

export interface PasoIntake {
  nombre: string;
  mensaje: string;
}

export interface IntakeResponse {
  expediente_id: string | null;
  estado: string;                 // "PENDIENTE_REVISION" | "REQUIERE_INFO" | ...
  pasos: PasoIntake[];
  campos_no_legibles: string[];
  campos_pendientes: string[];    // ["nic", "numero_radicado"] cuando GEMA los pide
}

export interface IntakeRequest {
  empresa: string;
  nic: string;
  numero_radicado: string;
  mensaje?: string;
  archivos: File[];
}

export async function intakeVisual(
  body: IntakeRequest
): Promise<IntakeResponse> {
  const fd = new FormData();
  fd.append("empresa", body.empresa);
  if (body.nic) fd.append("nic", body.nic);
  if (body.numero_radicado) fd.append("numero_radicado", body.numero_radicado);
  if (body.mensaje) fd.append("mensaje", body.mensaje);
  for (const f of body.archivos) {
    fd.append("archivos", f, f.name);
  }

  // Usamos `fetch` directo (no axios) para este endpoint: axios v1 con un
  // default `Content-Type: application/json` global a veces serializa
  // FormData como JSON (perdiendo el body), y forzar el header al valor
  // "multipart/form-data" no garantiza que axios añada el boundary.
  // Con `fetch` el navegador genera el header correcto sin ambigüedades.
  const url = baseURL + "/expedientes/intake";
  const csrf = leerCookie(CSRF_COOKIE);
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 300_000); // 5 min

  let resp: Response;
  try {
    resp = await fetch(url, {
      method: "POST",
      body: fd,
      // No se fija Content-Type: el navegador pone multipart con boundary.
      headers: csrf ? { "X-CSRF-Token": csrf } : undefined,
      credentials: "include",
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }

  if (!resp.ok) {
    let detalle: unknown;
    try {
      detalle = await resp.json();
    } catch {
      detalle = await resp.text();
    }
    if (resp.status === 401) window.dispatchEvent(new Event(EVENTO_SESION_EXPIRADA));
    const err = new Error(
      `intake fallido (${resp.status}): ${
        typeof detalle === "string" ? detalle : JSON.stringify(detalle)
      }`
    );
    (err as { response?: { status: number; data?: unknown } }).response = {
      status: resp.status,
      data: detalle,
    };
    throw err;
  }
  return (await resp.json()) as IntakeResponse;
}

export async function consultarExpediente(
  id: string,
  pregunta: string,
  autor: string
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/expedientes/${id}/consultar`,
    { pregunta, autor }
  );
  return data;
}

export interface IntencionDetectada {
  tipo:
    | "CONSULTAR"
    | "APORTAR_INFO"
    | "CAMBIAR_DECISION"
    | "REFORMULAR_CARTA"
    | "APROBAR"
    | "RECHAZAR"
    | "AYUDA";
  confianza: number;
  razon: string;
}

export interface ChatResponse {
  expediente: ExpedienteAnalizado;
  intencion: IntencionDetectada;
  sugerencias: string[];
}

export async function chatExpediente(
  id: string,
  mensaje: string,
  autor: string,
): Promise<ChatResponse> {
  const { data } = await cliente.post<ChatResponse>(
    `/expedientes/${id}/chat`,
    { mensaje, autor },
  );
  return data;
}

export interface HerramientaEvento {
  nombre: string;
  fase: "inicio" | "fin";
  ok?: boolean;
  resumen?: string;
}

export interface ChatStreamCallbacks {
  onIntencion?: (intencion: IntencionDetectada) => void;
  onChunk?: (texto: string) => void;
  onHerramienta?: (herramienta: HerramientaEvento) => void;
  onDone?: (data: { expediente: ExpedienteAnalizado; sugerencias: string[] }) => void;
  onError?: (mensaje: string) => void;
}

/**
 * Llama al endpoint /chat/stream y emite los eventos SSE como callbacks.
 *
 * Maneja Server-Sent Events parseando manualmente el body del fetch.
 * Compatible con bearer tokens (EventSource nativo no soporta headers
 * custom, por eso usamos fetch + ReadableStream).
 */
export async function chatExpedienteStream(
  id: string,
  mensaje: string,
  autor: string,
  callbacks: ChatStreamCallbacks,
): Promise<void> {
  const baseURL = (cliente.defaults.baseURL ?? "").replace(/\/$/, "");
  const url = `${baseURL}/expedientes/${id}/chat/stream`;
  const csrf = leerCookie(CSRF_COOKIE);
  const resp = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(csrf ? { "X-CSRF-Token": csrf } : {}),
    },
    credentials: "include",
    body: JSON.stringify({ mensaje, autor }),
  });

  if (!resp.ok || !resp.body) {
    const texto = await resp.text().catch(() => "");
    callbacks.onError?.(`HTTP ${resp.status}: ${texto || resp.statusText}`);
    return;
  }

  const reader = resp.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // SSE: eventos separados por línea en blanco. Cada evento puede
    // tener "event: <nombre>" y "data: <payload>".
    let limite: number;
    while ((limite = buffer.indexOf("\n\n")) !== -1) {
      const bloque = buffer.slice(0, limite);
      buffer = buffer.slice(limite + 2);
      procesarBloqueSse(bloque, callbacks);
    }
  }
  // Procesar cualquier bloque residual.
  if (buffer.trim()) procesarBloqueSse(buffer.trim(), callbacks);
}

function procesarBloqueSse(bloque: string, cb: ChatStreamCallbacks): void {
  let event = "message";
  let dataLine = "";
  for (const linea of bloque.split("\n")) {
    if (linea.startsWith("event:")) {
      event = linea.slice(6).trim();
    } else if (linea.startsWith("data:")) {
      dataLine += linea.slice(5).trim();
    }
  }
  if (!dataLine) return;
  let datos: unknown;
  try {
    datos = JSON.parse(dataLine);
  } catch {
    // Si no es JSON, lo pasamos como texto plano.
    datos = dataLine;
  }
  switch (event) {
    case "intencion":
      cb.onIntencion?.(datos as IntencionDetectada);
      break;
    case "chunk":
      cb.onChunk?.(String(datos ?? ""));
      break;
    case "herramienta":
      cb.onHerramienta?.(datos as HerramientaEvento);
      break;
    case "done":
      cb.onDone?.(datos as { expediente: ExpedienteAnalizado; sugerencias: string[] });
      break;
    case "error":
      cb.onError?.(String(datos ?? "Error desconocido"));
      break;
  }
}

export async function aportarInfo(
  id: string,
  deltas: Record<string, unknown>,
  autor: string,
  justificacion = ""
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/expedientes/${id}/aportar`,
    { deltas, autor, justificacion }
  );
  return data;
}

export async function solicitarCambioDecision(
  id: string,
  nuevaDecisionPropuesta: string,
  justificacion: string,
  autor: string
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/expedientes/${id}/cambio-decision`,
    {
      nueva_decision_propuesta: nuevaDecisionPropuesta,
      justificacion,
      autor,
    }
  );
  return data;
}

export async function reformularCarta(
  id: string,
  seccion: string,
  instruccion: string,
  autor: string
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/expedientes/${id}/reformular-carta`,
    { seccion, instruccion, autor }
  );
  return data;
}

export async function regenerarCarta(
  id: string,
  autor: string
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/expedientes/${id}/regenerar-carta`,
    { autor }
  );
  return data;
}

export async function auditarConLLM(
  id: string,
  autor = "auditor"
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/expedientes/${id}/auditar-llm`,
    { autor }
  );
  return data;
}

export async function aprobarExpediente(
  id: string,
  revisorId: string,
  motivo = ""
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/expedientes/${id}/aprobar`,
    { revisor_id: revisorId, motivo }
  );
  return data;
}

export async function rechazarExpediente(
  id: string,
  revisorId: string,
  motivo: string
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/expedientes/${id}/rechazar`,
    { revisor_id: revisorId, motivo }
  );
  return data;
}

/** F2 Notifica al cliente la decisión final. Requiere
 *  que el expediente esté APROBADO y sin placeholders pendientes. */
export async function notificarExpediente(
  id: string,
  revisorId: string,
  motivo = "Notificación al cliente."
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/expedientes/${id}/notificar`,
    { revisor_id: revisorId, motivo }
  );
  return data;
}

// ─── CRM PQRS (Postgres en RDS, read-only) y WS de cliente ──────── //

export interface CrmPqrPendienteItem {
  rad_open: string;
  rad_mercurio?: string | null;
  nic?: string | null;
  cliente?: string | null;
  fecha_radicacion?: string | null;
  fecha_vencimiento?: string | null;
  tipo_pqr?: string | null;
  nombre_paso?: string | null;
  dias_habiles?: number | null;
  requiere_clasificacion_gema: boolean;
  // Bandeja unificada (Fase A)
  expediente_id?: string | null;
  estado_expediente?: string | null;
  decision?: string | null;
  articulos?: string[] | null;
  confianza_motor?: number | null;
  valor_a_favor_cop?: number | null;
  estado_cola?: "PENDIENTE" | "PROCESANDO" | "OK" | "FALLO" | "OMITIDO" | null;
  intentos_cola?: number | null;
  ultimo_error_cola?: string | null;
  /** Cantidad de placeholders que el operador aún no ha aportado. > 0
   *  fuerza la fila al segmento "Pendiente info" aunque el motor ya haya
   *  decidido (caso IMPROCEDENTE con datos faltantes para la carta). */
  placeholders_pendientes_count?: number;
  /** True cuando el motor recomienda abrir orden de servicio antes de
   *  notificar la decisión. Pertenece al segmento "Visita técnica" con
   *  prioridad sobre "Pendiente info" porque sin la visita no hay datos
   *  para llenar la carta. */
  requiere_visita_tecnica?: boolean;
  tipo_orden_visita?: string | null;
  /** True si el auditor LLM detectó algo que EXIGE revisión humana (carta
   *  incoherente con la decisión, alerta omitida validada, o riesgo jurídico
   *  con confianza). El operador no debería aprobar de rutina. */
  gating_escalar?: boolean;
  /** Severidad del gating del auditor: "critica" | "alta" | "media". null si
   *  no escala o si aún no hay auditoría LLM del caso. */
  gating_severidad?: 'critica' | 'alta' | 'media' | null;
}

export interface CrmPqrPendientesResponse {
  total: number;
  items: CrmPqrPendienteItem[];
}

export interface CrmAdjuntoItem {
  nombre_archivo: string;
  ruta_archivo: string;
  tipo_archivo?: string | null;
  tamano_archivo?: number | null;
}

export interface CrmPqrDetalleResponse {
  rad_open: string;
  rad_mercurio?: string | null;
  nic?: string | null;
  cliente?: string | null;
  direccion?: string | null;
  fecha_radicacion?: string | null;
  fecha_vencimiento?: string | null;
  asunto?: string | null;
  tipo_pqr?: string | null;
  nombre_paso?: string | null;
  estado?: string | null;
  dias_habiles?: number | null;
  medio_atencion?: string | null;
  observaciones?: string | null;
  cuerpo_oficina_virtual?: string | null;
  lectura_oficina_virtual?: string | null;
  observaciones_orden_servicio: string;
  adjuntos: CrmAdjuntoItem[];
  requiere_clasificacion_gema: boolean;
}

export interface PerfilClienteWsResponse {
  nic: string;
  nombre?: string | null;
  direccion?: string | null;
  estrato?: number | null;
  fecha_factura_reclamada?: string | null;
  importe_factura_reclamada?: number | null;
  estado_factura_reclamada?: string | null;
  lectura_factura?: number | null;
  lectura_factura_sugerida?: number | null;
  consumo_reclamado_kwh?: number | null;
  historial: Array<{ periodo: string; kwh_facturado: number; estimado: boolean }>;
  advertencias: string[];
}

export async function listarPendientesCrm(params: {
  limite?: number;
  tipos?: string;
  area?: string;
  nic?: string;
  radicado?: string;
  fecha_desde?: string;
  fecha_hasta?: string;
  incluir_cerrados?: boolean;
} = {}): Promise<CrmPqrPendientesResponse> {
  const { data } = await cliente.get<CrmPqrPendientesResponse>(
    "/pqr/crm/pendientes",
    {
      params: {
        limite: params.limite ?? 100,
        tipos: params.tipos,
        area: params.area,
        nic: params.nic || undefined,
        radicado: params.radicado || undefined,
        fecha_desde: params.fecha_desde || undefined,
        fecha_hasta: params.fecha_hasta || undefined,
        incluir_cerrados: params.incluir_cerrados || undefined,
      },
    }
  );
  return data;
}

export async function obtenerPqrCrm(rad_open: string): Promise<CrmPqrDetalleResponse> {
  const { data } = await cliente.get<CrmPqrDetalleResponse>(`/pqr/crm/${rad_open}`);
  return data;
}

export async function obtenerPerfilCliente(
  nic: string,
  fecha_radicacion?: string
): Promise<PerfilClienteWsResponse> {
  const { data } = await cliente.get<PerfilClienteWsResponse>(
    `/clientes/${nic}/perfil`,
    { params: fecha_radicacion ? { fecha_radicacion } : undefined }
  );
  return data;
}

export async function ingestarPqrCrm(rad_open: string): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/pqr/crm/${rad_open}/ingestar`
  );
  return data;
}

// ─── Auto-ingesta ─────────────────────────────────────── //

export interface IngestaMetricas {
  pendientes: number;
  procesando: number;
  ok_hoy: number;
  fallo_hoy: number;
  omitido_hoy: number;
  pendiente_info_hoy: number;
  ts_dia: string;
  worker_activo: boolean;
  configuracion: {
    intervalo_seg: number | null;
    batch_size: number | null;
    concurrencia: number | null;
    ventana_dias: number | null;
    limite_diario: number | null;
  };
}

export interface IngestaColaItem {
  radicado: string;
  empresa: string;
  estado: "PENDIENTE" | "PROCESANDO" | "OK" | "FALLO" | "OMITIDO";
  intentos: number;
  ultimo_error: string | null;
  expediente_id: string | null;
  estado_expediente: string | null;
  decision: string | null;
  encolado_en: string | null;
  procesado_en: string | null;
}

export interface IngestaColaResponse {
  total: number;
  items: IngestaColaItem[];
}

export async function obtenerIngestaMetricas(): Promise<IngestaMetricas> {
  const { data } = await cliente.get<IngestaMetricas>("/ingesta/metricas");
  return data;
}

export async function listarIngestaCola(params: {
  estado?: string;
  empresa?: string;
  limit?: number;
} = {}): Promise<IngestaColaResponse> {
  const { data } = await cliente.get<IngestaColaResponse>("/ingesta/cola", {
    params: {
      estado: params.estado || undefined,
      empresa: params.empresa || undefined,
      limit: params.limit ?? 200,
    },
  });
  return data;
}

export async function reintentarIngesta(radicado: string): Promise<void> {
  await cliente.post(`/ingesta/reintentar/${radicado}`);
}

// ─── Audit log SSPD ───────────────────────────────────────────────── //

export interface EventoAuditoriaItem {
  evento_id: string;
  secuencia: number;
  tipo_evento: string;
  actor: string;
  descripcion: string;
  timestamp_utc: string;
  payload: Record<string, unknown>;
  hash_evento: string;
  hash_anterior: string;
}

export interface AuditLogResponse {
  expediente_id: string;
  total: number;
  cadena_integra: boolean;
  eventos: EventoAuditoriaItem[];
}

export async function obtenerAuditLog(
  expediente_id: string
): Promise<AuditLogResponse> {
  const { data } = await cliente.get<AuditLogResponse>(
    `/expedientes/${expediente_id}/audit-log`
  );
  return data;
}

export async function aportarPlaceholdersCarta(
  expediente_id: string,
  valores: Record<string, string>,
  autor: string
): Promise<ExpedienteAnalizado> {
  const { data } = await cliente.post<ExpedienteAnalizado>(
    `/expedientes/${expediente_id}/placeholders-carta`,
    { valores, autor }
  );
  return data;
}

export async function descargarCartaDocx(
  expediente_id: string,
  filename = "carta.docx"
): Promise<void> {
  // Descarga real → preview=false (default). El backend registra
  // CARTA_DESCARGADA o CARTA_DESCARGADA_FALLBACK en el audit log.
  const blob = await obtenerCartaDocxBlob(expediente_id, false);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Devuelve el DOCX como Blob — útil para vista previa en el frontend
 * con `docx-preview` sin gatillar descarga (preview fiel
 * de la plantilla en el expediente).
 *
 * @param preview true (default) cuando solo se va a renderizar in-place
 *   con docx-preview. El backend NO registra evento de audit en ese
 *   caso (fix C — deduplicación de CARTA_DESCARGADA_FALLBACK).
 */
export async function obtenerCartaDocxBlob(
  expediente_id: string,
  preview: boolean = true,
): Promise<Blob> {
  const response = await cliente.get(
    `/expedientes/${expediente_id}/carta.docx`,
    { responseType: "blob", params: preview ? { preview: 1 } : undefined },
  );
  return new Blob([response.data], {
    type:
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  });
}

export default cliente;
